import React, { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Container, Eyebrow, PageHero } from '../components/ui/Primitives';
import { Seo } from '../components/seo/Seo';
import { images } from '../data/site';
import { pageSeo } from '../data/seo';
import { breadcrumbSchema, localBusinessSchema, organizationSchema, webPageSchema } from '../utils/schema';
const seo = pageSeo.contact;
const crumbs = [{
  label: 'Home',
  path: '/'
}, {
  label: 'Contact',
  path: '/contact'
}];
type FormState = {
  name: string;
  email: string;
  phone: string;
  message: string;
};
type Errors = Partial<Record<keyof FormState, string>>;
const initialForm: FormState = {
  name: '',
  email: '',
  phone: '',
  message: ''
};
export function Contact() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent'>('idle');
  const update = (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Errors = {};
    if (!form.name.trim()) nextErrors.name = 'Please enter your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) nextErrors.email = 'Please enter a valid email address.';
    if (form.message.trim().length < 10) nextErrors.message = 'Please add a little more detail.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setStatus('submitting');
    window.setTimeout(() => {
      setStatus('sent');
      setForm(initialForm);
    }, 700);
  };
  const fieldClass = 'w-full border-b border-line bg-transparent py-3 text-[16px] text-ink outline-none transition-colors placeholder:text-mist/70 focus:border-ink';
  return <>
      <Seo title={seo.title} description={seo.description} path={seo.path} keywords={seo.keywords} image={images.team} schemas={[organizationSchema, localBusinessSchema, webPageSchema({
      ...seo
    }), breadcrumbSchema(crumbs)]} data-id="element-158" data-mp="1m6293hcykdqd" />

      <PageHero eyebrow="Contact" title="Contact NexLaw" subtitle="Get in touch to learn more about NexLaw and how our lawyer-reviewed, AI-supported services work." crumbs={crumbs} data-id="element-159" data-mp="1r4dgzmjp0e5y" />

      <Container className="py-16 md:py-24 lg:py-28" data-id="element-160" data-mp="1d6oxoj59xf30">
        <div className="grid gap-14 md:grid-cols-12 md:gap-20" data-id="element-161" data-mp="1d88eqbyr7u5u">
          <div className="md:col-span-7" data-id="element-162" data-mp="18ffd3wyl11xz">
            <h2 className="sr-only" data-id="element-163" data-mp="18eno54zhq2go">Send NexLaw a message</h2>
            {status === 'sent' ? <div role="status" className="rounded-sm border border-line bg-white p-7 sm:p-10" data-id="element-164" data-mp="kojn95dosgtd">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-ink text-paper" data-id="element-165" data-mp="2rewmbjv4xxv0">
                  <CheckIcon sx={{
                fontSize: 20
              }} data-id="element-166" data-mp="2w5msgzbdy4qr" />
                </span>
                <h2 className="mt-6 font-display text-[26px] font-medium tracking-tight text-ink" data-id="element-167" data-mp="2hgqnxc8jf9uw">
                  Thank you — your message is on its way.
                </h2>
                <p className="mt-3 max-w-prose text-[16px] leading-relaxed text-graphite" data-id="element-168" data-mp="2md7bo5btrm7g">
                  Someone from NexLaw will get back to you shortly.
                </p>
                <button type="button" onClick={() => setStatus('idle')} className="mt-8 border-b border-ink pb-1 text-[15px] font-medium text-ink transition-colors hover:border-navy hover:text-navy" data-id="element-169" data-mp="3f3fxc75kkoqs">
                  Send another message
                </button>
              </div> : <form onSubmit={handleSubmit} noValidate className="space-y-8 md:space-y-10" data-id="element-170" data-mp="1trxnb2gz0dty">
                <div data-id="element-171" data-mp="29yaqo7eh491p">
                  <label htmlFor="name" className="text-[12px] uppercase tracking-[0.16em] text-mist" data-id="element-172" data-mp="29zlqxx8pmyo2">
                    Name
                  </label>
                  <input id="name" name="name" type="text" value={form.name} onChange={update('name')} autoComplete="name" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'name-error' : undefined} className={fieldClass} placeholder="Your full name" data-id="element-173" data-mp="208hbkwf4hbsp" />
                  {errors.name && <p id="name-error" className="mt-2 text-[13px] text-navy" data-id="element-174" data-mp="17jk7bx7qxfiy">
                      {errors.name}
                    </p>}
                </div>

                <div data-id="element-175" data-mp="13k1mf1fxd4g4">
                  <label htmlFor="email" className="text-[12px] uppercase tracking-[0.16em] text-mist" data-id="element-176" data-mp="3f5k9ckac6a8h">
                    Email
                  </label>
                  <input id="email" name="email" type="email" value={form.email} onChange={update('email')} autoComplete="email" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} className={fieldClass} placeholder="you@example.com" data-id="element-177" data-mp="3o5gqm0uec6ti" />
                  {errors.email && <p id="email-error" className="mt-2 text-[13px] text-navy" data-id="element-178" data-mp="fpnbnqfpr3ek">
                      {errors.email}
                    </p>}
                </div>

                <div data-id="element-179" data-mp="2hilbw7btky9j">
                  <label htmlFor="phone" className="text-[12px] uppercase tracking-[0.16em] text-mist" data-id="element-180" data-mp="2hhsbdo3gf4zu">
                    Phone <span className="normal-case tracking-normal" data-id="element-181" data-mp="3fueedgymf4fh">(optional)</span>
                  </label>
                  <input id="phone" name="phone" type="tel" value={form.phone} onChange={update('phone')} autoComplete="tel" className={fieldClass} placeholder="(000) 000-0000" data-id="element-182" data-mp="3ttx9982f8j9u" />
                </div>

                <div data-id="element-183" data-mp="gehdke8pbhhc">
                  <label htmlFor="message" className="text-[12px] uppercase tracking-[0.16em] text-mist" data-id="element-184" data-mp="gcxzmuaipiiq">
                    Message
                  </label>
                  <textarea id="message" name="message" rows={5} value={form.message} onChange={update('message')} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'message-error' : undefined} className={`${fieldClass} resize-none`} placeholder="Tell us a little about what you need." data-id="element-185" data-mp="qcos6qfipng6" />
                  {errors.message && <p id="message-error" className="mt-2 text-[13px] text-navy" data-id="element-186" data-mp="m835goezoc10">
                      {errors.message}
                    </p>}
                </div>

                <button type="submit" disabled={status === 'submitting'} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-7 py-3.5 text-[14.5px] font-medium text-paper transition-colors hover:bg-navy disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:py-3" data-id="element-187" data-mp="caozjiqoz5my">
                  {status === 'submitting' ? 'Sending…' : 'Send message'}
                </button>
              </form>}
          </div>

          <aside aria-labelledby="contact-details-heading" className="md:col-span-5" data-id="element-188" data-mp="istgewrllcgx">
            <h2 id="contact-details-heading" className="sr-only" data-id="element-189" data-mp="ds5ng5jt6lk2">
              Contact details
            </h2>
            <div className="border-t border-ink pt-6" data-id="element-190" data-mp="8skgb7uhj6w9">
              <Eyebrow data-id="element-191" data-mp="4s0is360219l">Direct</Eyebrow>
              <ul className="mt-5 space-y-3 text-[16px] text-graphite" data-id="element-192" data-mp="4t2739c10k99">
                <li data-id="element-193" data-mp="1k46fqg09ln">
                  <a href="mailto:hello@nexlaw.ca" className="transition-colors hover:text-ink" data-id="element-194" data-mp="10jwsdbl94g">
                    hello@nexlaw.ca
                  </a>
                </li>
                <li data-id="element-195" data-mp="3mabnv33gkhgt">nexlaw.ca</li>
              </ul>
            </div>

            <div className="mt-12 border-t border-line pt-6" data-id="element-196" data-mp="3h8cgzj8if2f4">
              <Eyebrow data-id="element-197" data-mp="3d7sjufnxrysz">Jurisdictions</Eyebrow>
              <p className="mt-5 max-w-prose text-[16px] leading-relaxed text-graphite" data-id="element-198" data-mp="3d93nmfhdt6k3">
                NexLaw is currently focused on Ontario and Quebec.
              </p>
            </div>

            <div className="mt-12 border-t border-line pt-6" data-id="element-199" data-mp="1wqbt9tqtwbqi">
              <Eyebrow data-id="element-200" data-mp="21qpogb7bzr74">Please note</Eyebrow>
              <p className="mt-5 max-w-prose text-[15px] leading-relaxed text-mist" data-id="element-201" data-mp="21rrcdg9g4869">
                Contacting NexLaw does not create a lawyer-client relationship. Please
                do not share confidential details until a relationship is confirmed.
              </p>
            </div>
          </aside>
        </div>
      </Container>
    </>;
}